import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { UserService, UserServiceModel } from '../origami-api/services/user.service';
import { ErrorCode, GroupType, OrigamiModuleType, RoleLevel, UserValidationType } from '../origami-api/constants';
import { Action } from '../origami-api/constants/actions';
import { UserPreferences } from './user-preferences';
import { UserGroupService, UserGroupServiceModel } from '@app/origami-api/services/user-group.service';
import { SessionService } from '@app/session/session.service';
import { getServerConfig } from '@app/origami-api/server-config';
import { TiFeatures } from '@app/origami-api/constants/features';
import {
  AdditionalRequestOptions,
  RequestValidationConfig,
} from '@app/origami-api/services/base/request-proxy/request-proxy.class';
import { TimeZoneInfo } from '@app/origami-api/utils/topic-utils';

export interface ValidationData {
  UserName?: string;
  UserPassword?: string;
  TransactionId?: string;
  ValidationType?: UserValidationType;
}

export class User {
  get superAdmin() {
    return this.details.RoleLevel >= RoleLevel.SUPERADMIN;
  }
  get adminOrHigher() {
    return this.details.RoleLevel >= RoleLevel.ADMIN;
  }
  get managerOrHigher() {
    return this.details.RoleLevel >= RoleLevel.MANAGER;
  }
  get editorOrLower() {
    return this.details.RoleLevel <= RoleLevel.EDITOR;
  }
  get editorOrHigher() {
    return this.details.RoleLevel >= RoleLevel.EDITOR;
  }
  get manager() {
    return this.details.RoleLevel === RoleLevel.MANAGER;
  }
  get editor() {
    return this.details.RoleLevel === RoleLevel.EDITOR;
  }
  get readerOrHigher() {
    return this.details.RoleLevel >= RoleLevel.READER;
  }
  get reader() {
    return this.details.RoleLevel === RoleLevel.READER;
  }
  get id() {
    return this.details.UserId;
  }
  get details() {
    return this.options.details;
  }
  get userGroups() {
    return this.groups;
  }
  get ableToEditSiteAndContacts() {
    if (
      !this.superAdmin &&
      this.options.sessionService.settings.general.document.dontAllowUsersToEditSiteContacts.get()
    ) {
      return (
        getServerConfig().serverCfg.disabledFeatures.includes(TiFeatures.CtmsIntegration) ||
        !this.options.sessionService.room.info.IsCTMSIntegrationEnabled
      );
    }
    return true;
  }
  get regulatoryReviewer() {
    if (this.adminOrHigher) {
      return false;
    }

    return this.options.sessionService.room.info.IsUserSSUApprover;
  }
  get sponsorReviewer() {
    if (this.adminOrHigher) {
      return false;
    }
    return !!this.details.AdditionalInfo?.IsSponsor;
  }
  // eTMF members who can edit&create contact and site ONLY in eTMR
  get isUserSiteTeamGroup(): boolean {
    return this.groups?.some((group: UserGroupServiceModel.UserGroup) =>
      [GroupType.StudyStartupTeamRoom].includes(group.GroupTypeId),
    );
  }

  // SSU members who can edit&create contact and site in eTMR&SSUU
  get isUserSiteActivationMembers(): boolean {
    return this.groups?.some((group: UserGroupServiceModel.UserGroup) =>
      [GroupType.StartupSpecialists].includes(group.GroupTypeId),
    );
  }
  get isUserSSUMember() {
    return this.options.sessionService.room.info.IsUserSSUMember;
  }
  get timeZone(): TimeZoneInfo {
    return this.options.details.UserTimeZone;
  }

  private _isRestrictedDocumentsCreation: boolean = null;
  get isRestrictedDocumentsCreation(): boolean {
    if (this._isRestrictedDocumentsCreation === null) {
      this._isRestrictedDocumentsCreation = this.editor && !!this.hasAction(Action.RestrictDocumentsCreation);
    }
    return this._isRestrictedDocumentsCreation;
  }

  readonly preferences: UserPreferences;
  private groups: UserGroupServiceModel.UserGroup[];

  constructor(
    private options: {
      currentRoomId: number;
      details: UserServiceModel.User;
      sessionService: SessionService;
      userService: UserService;
      userGroupService: UserGroupService;
    },
  ) {
    this.preferences = new UserPreferences(options.details, options.userService);
  }

  getGroups(): Observable<UserGroupServiceModel.UserGroup[]> {
    if (this.groups) {
      return of(this.groups);
    }
    return this.options.userGroupService.getUserGroups({ userName: '', siteId: this.options.currentRoomId }).pipe(
      map((res) => res.Result || []),
      catchError(() => of(<UserGroupServiceModel.UserGroup[]>[])),
      tap((groups) => (this.groups = groups)),
    );
  }

  isModuleAccessible(moduleType: OrigamiModuleType): boolean {
    return (
      this.details.AdditionalInfo.Modules &&
      this.details.AdditionalInfo.Modules.filter((item) => item.ModuleType === moduleType).length !== 0
    );
  }

  hasAction(action: Action): boolean {
    return this.details.Actions.filter((item) => item.ActionId === action).length === 1;
  }

  getIdentityValidationType(): UserValidationType {
    return this.details.IsSsoEnabled || getServerConfig().IamEnabled
      ? UserValidationType.Sso
      : UserValidationType.Internal;
  }

  isSimpleAuthOn() {
    return (
      getServerConfig().serverCfg.centralAuthentication !== 'cas' ||
      this.getIdentityValidationType() === UserValidationType.Internal
    );
  }

  public getRequestValidationConfig(
    params,
    request,
    optionsToOverride?: AdditionalRequestOptions,
    additionalSilentErrors?: ErrorCode[],
  ): RequestValidationConfig {
    const isSimpleAuthOn = this.isSimpleAuthOn();
    const initialValidationData = {
      validationData: {
        ValidationType: isSimpleAuthOn ? UserValidationType.Internal : UserValidationType.Sso,
      },
    };
    const paramsWValidation = Object.assign(params, initialValidationData);
    let additionalRequestOptions: AdditionalRequestOptions = {
      //useValidationTransactionIdParam: true,
    };

    if (optionsToOverride && optionsToOverride.useSimpleValidationParamsDelivery) {
      additionalRequestOptions.useSimpleValidationParamsDelivery = optionsToOverride.useSimpleValidationParamsDelivery;
    } else {
      additionalRequestOptions.forceAdditionalRequest = isSimpleAuthOn;
      additionalRequestOptions.additionalRequest = request;
    }

    if (
      additionalRequestOptions.useSimpleValidationParamsDelivery ||
      (additionalRequestOptions.forceAdditionalRequest && additionalRequestOptions.additionalRequest)
    ) {
      additionalRequestOptions.isSimpleAuthMethod = true;
    }

    let reqConfig: RequestValidationConfig = {
      additionalRequestOptions: additionalRequestOptions,
      modifiedParams: paramsWValidation,
    };

    if (additionalSilentErrors && additionalSilentErrors.length) {
      reqConfig.modifiedSilentErrors = additionalSilentErrors;
    }

    return reqConfig;
  }
}
